import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Prepare Your Workspace',
    description: 'Establish a clean working area so every subsequent step starts from the same known state.',
    requirements: [
      {
        text: 'Switch to the lab user home directory and confirm your location',
        subitems: [
          'Run `cd /home/lab-user` followed by `pwd` and ensure the output is `/home/lab-user`',
          'Use `ls` to familiarise yourself with existing files'
        ]
      },
      {
        text: 'Optionally create a scratch directory for temporary experiments',
        subitems: ['`mkdir -p ~/lab-sandbox` and `cd ~/lab-sandbox` when you need a safe place to test ideas']
      },
      {
        text: 'Pick an editor and stick with it for the whole lab',
        subitems: ['`nano` is preinstalled and beginner friendly: `nano filename.sh`']
      },
      {
        text: 'Remember: mark every script executable after editing',
        subitems: ['`chmod +x scriptname.sh` enables you to run `./scriptname.sh` without invoking Bash manually']
      }
    ],
    verification: [
      'Running `pwd` shows /home/lab-user',
      'You can quickly return home with `cd ~`',
      'You know which editor you will use for the remaining steps'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Step 1 – Variables and Output',
    description: 'Create a simple script that stores a message in a variable and prints it with echo.',
    requirements: [
      {
        text: 'Create `/home/lab-user/step1.sh` with the Bash shebang at the very top'
      },
      {
        text: 'Declare a variable named `MESSAGE` containing the text `Shell scripting is fun!`'
      },
      {
        text: 'Print the variable value using `echo "$MESSAGE"` so the message appears on screen'
      },
      {
        text: 'Make the script executable (`chmod +x step1.sh`) and run it with `./step1.sh`'
      },
      {
        text: 'Knowledge check reflections',
        subitems: [
          'Why does the shebang matter when launching the script with `./step1.sh`?',
          'What do you observe if you echo `MESSAGE` without the `$` prefix?'
        ]
      }
    ],
    verification: [
      'The command `ls -l step1.sh` shows execute permissions (e.g., `-rwx`)',
      'Running `./step1.sh` prints `Shell scripting is fun!` exactly once'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Step 2 – Command Substitution and Arguments',
    description: 'Capture today’s date, create a directory named after it, and move the file provided as an argument.',
    requirements: [
      {
        text: 'Create `step2.sh` with the Bash shebang and optional `set -euo pipefail` guard'
      },
      {
        text: 'Store the ISO date while honoring the grader override: `TODAY="${TODAY_OVERRIDE:-$(date +%F)}"`'
      },
      {
        text: 'Create the directory with `mkdir -p "$TODAY"`'
      },
      {
        text: 'Check that an argument was supplied; show usage and exit if `$1` is empty'
      },
      {
        text: 'Move the provided file into the dated directory, preserving the original filename (`mv "$1" "$TODAY/$(basename "$1")"`)' 
      },
      {
        text: 'Knowledge check reflections',
        subitems: [
          'How would you prevent overwriting an existing file inside the dated directory?',
          'Which special parameter expands to the entire argument list (and why is it useful)?'
        ]
      }
    ],
    verification: [
      'After running `touch sample.log && ./step2.sh sample.log`, the file resides inside the dated folder',
      'Running `TODAY_OVERRIDE=/home/lab-user/test-date ./step2.sh sample.log` moves the file into /home/lab-user/test-date',
      'Running the script without arguments exits with a helpful usage message'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Step 3 – Redirecting Output Streams',
    description: 'Practice redirecting standard output and standard error to different log files.',
    requirements: [
      {
        text: 'Create `step3.sh` with a shebang (and optional strict mode) in `/home/lab-user`'
      },
      {
        text: 'Run `ls -l /etc/passwd /fake/directory` and redirect stdout to `success.log` and stderr to `errors.log` in the same command'
      },
      {
        text: 'Execute the script, then view both log files with `cat` or `less` to understand the captured output'
      },
      {
        text: 'Knowledge check reflections',
        subitems: [
          'Why does stderr use `2>` while stdout uses `>`?',
          'How would you change the redirects to append instead of overwrite?'
        ]
      }
    ],
    verification: [
      '`success.log` contains the `/etc/passwd` listing',
      '`errors.log` contains the “No such file or directory” message',
      'Re-running the script refreshes both logs as expected'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Step 4 – Loops and Conditionals',
    description: 'Use a `for` loop with pattern matching to rename only `.txt` files and leave other files untouched.',
    requirements: [
      {
        text: 'Create `step4.sh` with the Bash shebang (strict mode optional)'
      },
      {
        text: 'Iterate over every entry in the current directory with `for item in *; do ... done`'
      },
      {
        text: 'Inside the loop, match `.txt` files using `[[ $item == *.txt ]]` and rename them to `filename.txt.bak`'
      },
      {
        text: 'Test the script with `touch file1.txt file2.txt data.csv` before executing `./step4.sh`'
      },
      {
        text: 'Knowledge check reflections',
        subitems: [
          'What advantages do double brackets (`[[ ]]`) provide for pattern tests?',
          'How could you adjust the loop to search subdirectories for `.log` files?'
        ]
      }
    ],
    verification: [
      '`file1.txt.bak` and `file2.txt.bak` exist after running the script',
      '`data.csv` remains in place without a `.bak` suffix',
      'Re-running the script does not generate duplicate `.bak` files'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Step 5 – Log Backup Challenge',
    description: 'Combine variables, arguments, loops, and archiving to collect `.log` files into a datestamped tarball.',
    requirements: [
      {
        text: 'Create `step5.sh` with the Bash shebang and keep it in `/home/lab-user`'
      },
      {
        text: 'Validate that exactly one argument (a directory) is supplied before continuing'
      },
      {
        text: 'Ensure `/home/lab-user/backups` exists (`mkdir -p /home/lab-user/backups`)'
      },
      {
        text: 'Collect only `.log` files from the target directory (consider `find "$1" -type f -name "*.log" -print0` plus a loop)'
      },
      {
        text: 'Create `logs-YYYY-MM-DD.tar.gz` in `/home/lab-user/backups` and include just the `.log` files; handle the “no logs” case gracefully'
      },
      {
        text: 'Print the exact message `Log backup complete.` only after the archive succeeds'
      },
      {
        text: 'Knowledge check reflections',
        subitems: [
          'How would you rotate archives so only the seven newest files remain?',
          'What change would skip log files larger than 5 MB?'
        ]
      }
    ],
    verification: [
      'Running `./step5.sh logs_to_backup` creates `/home/lab-user/backups/logs-<date>.tar.gz`',
      '`tar -tzf` shows only `.log` entries inside the archive',
      'Script output is exactly `Log backup complete.`'
    ]
  }
];

const BasicShellScriptingInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handlePrevious = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);

    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }

    setCompletedChecks(newChecks);

    const allChecksCompleted = tasks[currentTaskIndex].verification.every(
      (_, idx) => newChecks.has(`${tasks[currentTaskIndex].id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      <div className="flex h-[1600px]">
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                  completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
                } text-[#1a1f2e]`}
              >
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>

            {currentTask.requirements.map((requirement, reqIndex) => (
              <div
                key={reqIndex}
                className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all"
              >
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>

            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div
                  className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                    completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                  }`}
                >
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm leading-[1.5]">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-8 py-5 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[#94a3b8] text-sm">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>

        <div className="flex-1 mx-[40px]">
          <div className="h-[6px] bg-[#232937] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#10b981] to-[#059669] rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handlePrevious}
            disabled={currentTaskIndex === 0}
            className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
              currentTaskIndex === 0
                ? 'bg-[#232937] text-[#475569] cursor-not-allowed'
                : 'bg-[#2d3748] text-[#e2e8f0] hover:bg-[#374151]'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={currentTaskIndex === tasks.length - 1}
            className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
              currentTaskIndex === tasks.length - 1
                ? 'bg-[#232937] text-[#475569] cursor-not-allowed'
                : 'bg-[#10b981] text-[#1a1f2e] hover:bg-[#059669]'
            }`}
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicShellScriptingInstructions;
